import { runDiscoveryRead } from "./discoveryReadQueue.js";
import { useEffect, useState } from "react";
import { runQuery, rowsToObjects } from "./analyticsClient.js";
import { discoveryTotalsSpec, findingValueLabel, organizationLabel, organizationMatrixSpec, organizationTopSpec } from "./discoveryQueries.js";
import { summarizeFindingInvestigations } from "./alertTriageClient.js";

export function assembleOrganization({ axis, keys, named, remainder, total }) {
    if (named.length > 48 || remainder.length > 18) throw new Error("The organization breakdown exceeded its supported buckets.");
    const rows = [...named, ...remainder.map((r) => ({ ...r, group: r.group === "mapped" ? "other:" : r.group === "multiple" ? "multiple:" : "" }))]
        .map((r) => ({ ...r, findings: Number(r.findings) })).filter((r) => r.findings > 0);
    if (rows.some((r) => !Number.isSafeInteger(r.findings)) || rows.reduce((n, r) => n + r.findings, 0) !== Number(total)) throw new Error("Finding counts changed while loading. Refresh the breakdown.");
    const present = new Set(rows.map((r) => r.group));
    const groups = [...keys, "other:", "multiple:", ""].filter((key) => present.has(key)).map((key) => ({ key, label: organizationLabel(key), constraint: { axis, value: key, ...(key === "other:" ? { exclude: keys } : {}) } }));
    const order = ["critical", "high", "medium", "low", "none", ""];
    const destinations = [...new Set(rows.map((r) => r.destination))].sort((a, b) => order.indexOf(a) - order.indexOf(b) || a.localeCompare(b)).map((key) => ({ key, value: key, label: findingValueLabel(key) }));
    if (destinations.length > 6) throw new Error("The organization breakdown exceeded its supported buckets.");
    return { groups, destinations, rows, total: Number(total), mappedFindings: rows.filter((r) => r.group.startsWith("value:") || r.group === "other:").reduce((n, r) => n + r.findings, 0) };
}
export function useOrganizationOverview({ settings, timeRange, contextKey, allowed }) {
    const { axis, minimum, destination, selection } = settings;
    const pinned = selection?.group?.startsWith("value:") ? selection.group : null;
    const key = JSON.stringify({ axis, minimum, destination, pinned, timeRange, contextKey, allowed });
    const [result, setResult] = useState(null);
    useEffect(() => {
        if (!allowed) return undefined;
        const controller = new AbortController();
        let active = true;
        const timeout = setTimeout(() => { controller.abort(); if (active) setResult({ key, status: "error", error: new Error("The organization query timed out.") }); }, 30000);
        const query = async (spec) => rowsToObjects(await runDiscoveryRead(() => runQuery(spec, { signal: controller.signal, requestContext: contextKey }), { signal: controller.signal }));
        (async () => {
            const options = { axis, minimum, destination, timeRange };
            const top = await query(organizationTopSpec(options));
            let keys = top.map((r) => r.value);
            if (pinned && !keys.includes(pinned)) keys = [...keys.slice(0, 7), pinned];
            const filters = minimum === "all" ? [] : [{ axis: "severity", value: minimum, minimum: true }];
            const named = keys.length ? await query(organizationMatrixSpec({ ...options, keys })) : [];
            const remainder = await query(organizationMatrixSpec({ ...options, keys, remainder: true }));
            const totals = await query(discoveryTotalsSpec(timeRange, filters));
            const data = assembleOrganization({ axis, keys, named, remainder, total: totals[0]?.findings ?? 0 });
            if (active && !controller.signal.aborted) setResult({ key, status: "ready", ...data });
        })().catch((error) => { if (active && !controller.signal.aborted) setResult({ key, status: "error", error }); }).finally(() => clearTimeout(timeout));
        return () => { active = false; clearTimeout(timeout); controller.abort(); };
    }, [key]);
    return !allowed ? { status: "denied" } : result?.key === key ? result : { status: "loading" };
}
export function useInvestigationSummary(contextKey, revision) {
    const key = JSON.stringify([contextKey, revision]);
    const [result, setResult] = useState(null);
    useEffect(() => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 30000);
        let active = true;
        summarizeFindingInvestigations({ signal: controller.signal }).then((data) => {
            if (active) setResult({ key, status: "ready", ...data });
        }, (error) => { if (active) setResult({ key, status: "error", error }); }).finally(() => clearTimeout(timeout));
        return () => { active = false; clearTimeout(timeout); controller.abort(); };
    }, [key]);
    return result?.key === key ? result : { status: "loading" };
}
